/**
 * Zoom-aware track simplification (Ramer–Douglas–Peucker), ported from Alinghi.
 *
 * Each point is scored once: the largest tolerance (Web Mercator world units) at which RDP would
 * still keep it. Drawing then drops points under a zoom threshold. A straight leg collapses to its
 * endpoints; a turn keeps the samples that bend away from the chord.
 *
 * Callers must simplify each already-split run (one color, one gap segment), never the raw track.
 * Run endpoints stay, so a color change is never dropped and neighbouring runs still meet.
 */

/** Allowed deviation of the simplified line from the samples, in screen pixels. */
export const TRACK_SIMPLIFY_TOLERANCE_PX = 0.5;

/** RDP spans are capped so a pathological track stays O(n · span) rather than O(n²). */
const MAX_RDP_SPAN = 20000;

/** Mapbox world size in pixels at zoom 0. */
const WORLD_PX_AT_Z0 = 512;

export type TrackImportance = WeakMap<object, number>;

type LatLngGetter = (p: unknown) => number | undefined;

function rdpSpan(xs: Float64Array, ys: Float64Array, a: number, b: number, imp: Float64Array): void {
  imp[a] = Infinity;
  imp[b] = Infinity;
  const stack: number[] = [a, b, Infinity];
  while (stack.length > 0) {
    const cap = stack.pop() as number;
    const hi = stack.pop() as number;
    const lo = stack.pop() as number;
    if (hi - lo < 2) continue;
    const ax = xs[lo];
    const ay = ys[lo];
    const dx = xs[hi] - ax;
    const dy = ys[hi] - ay;
    const len2 = dx * dx + dy * dy;
    let best = lo + 1;
    let bestD2 = -1;
    for (let k = lo + 1; k < hi; k++) {
      const px = xs[k] - ax;
      const py = ys[k] - ay;
      let d2: number;
      if (len2 > 0) {
        let t = (px * dx + py * dy) / len2;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const ex = px - t * dx;
        const ey = py - t * dy;
        d2 = ex * ex + ey * ey;
      } else {
        d2 = px * px + py * py;
      }
      if (d2 > bestD2) {
        bestD2 = d2;
        best = k;
      }
    }
    // Capped by the parent split so importance is monotone: thresholding at any tolerance
    // yields exactly the RDP result at that tolerance.
    const d = Math.min(Math.sqrt(bestD2), cap);
    imp[best] = d;
    stack.push(lo, best, d, best, hi, d);
  }
}

/**
 * RDP importance for every point of a time-ordered track. Points without coordinates are always kept.
 * `into` lets several boats share one lookup.
 */
export function computeTrackImportance(
  track: readonly unknown[],
  getLat: LatLngGetter,
  getLng: LatLngGetter,
  into: TrackImportance = new WeakMap()
): TrackImportance {
  const n = track?.length ?? 0;
  if (n === 0) return into;
  const xs = new Float64Array(n);
  const ys = new Float64Array(n);
  const valid = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const lat = getLat(track[i]);
    const lng = getLng(track[i]);
    if (lat === undefined || lng === undefined || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    xs[i] = (lng + 180) / 360;
    ys[i] = 0.5 - Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) / (2 * Math.PI);
    valid[i] = Number.isFinite(ys[i]) ? 1 : 0;
  }
  const imp = new Float64Array(n);
  let start = -1;
  for (let i = 0; i < n; i++) {
    if (!valid[i]) {
      imp[i] = Infinity;
      start = -1;
      continue;
    }
    if (start < 0) start = i;
    const endOfSpan = i === n - 1 || !valid[i + 1];
    if (endOfSpan || i - start >= MAX_RDP_SPAN) {
      rdpSpan(xs, ys, start, i, imp);
      start = endOfSpan ? -1 : i;
    }
  }
  for (let i = 0; i < n; i++) {
    const p = track[i];
    if (p && typeof p === "object") into.set(p, imp[i]);
  }
  return into;
}

/**
 * Importance cached on track content (length + first/last point identity + coordinate fields).
 * Layers receive a fresh array every pass, so array identity alone would recompute.
 */
export function createTrackImportanceCache() {
  let sigLen = -1;
  let sigFirst: unknown = null;
  let sigLast: unknown = null;
  let sigKey = "";
  let value: TrackImportance | null = null;
  return (track: readonly unknown[], getLat: LatLngGetter, getLng: LatLngGetter, key: string): TrackImportance => {
    const n = track?.length ?? 0;
    if (value && n === sigLen && key === sigKey && track[0] === sigFirst && track[n - 1] === sigLast) {
      return value;
    }
    value = computeTrackImportance(track, getLat, getLng);
    sigLen = n;
    sigKey = key;
    sigFirst = track[0];
    sigLast = n > 0 ? track[n - 1] : null;
    return value;
  };
}

/**
 * World-unit tolerance for a zoom. Sized for the top of the even-zoom band, so zooming in
 * without a redraw stays within {@link TRACK_SIMPLIFY_TOLERANCE_PX}.
 */
export function trackSimplifyTolerance(zoom: number): number {
  const z = Number.isFinite(zoom) ? zoom : 16;
  const bandTop = Math.floor(z / 2) * 2 + 2;
  return TRACK_SIMPLIFY_TOLERANCE_PX / (WORLD_PX_AT_Z0 * Math.pow(2, bandTop));
}

/**
 * Drop samples of one drawn run that are invisible at `tolerance`. Endpoints are always kept,
 * so a color-run boundary stays. Points the lookup does not know (playhead endpoint) are kept.
 * Returns the input array when nothing was dropped.
 */
export function simplifyTrackRun<T>(
  run: T[],
  importance: TrackImportance | null | undefined,
  tolerance: number
): T[] {
  const n = run?.length ?? 0;
  if (!importance || n <= 2 || !(tolerance > 0)) return run;
  const out: T[] = [run[0]];
  for (let i = 1; i < n - 1; i++) {
    const p = run[i];
    const v = p && typeof p === "object" ? importance.get(p as object) : undefined;
    if (v === undefined || v >= tolerance) out.push(p);
  }
  out.push(run[n - 1]);
  return out.length === n ? run : out;
}
